import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import logging from "../utils/logging";
import { playAlertSound, playSoundForAlert } from "../utils/alertSound";
import { distanceMeters } from "../utils/geo";
import { useWebSocket } from "./useWebSocket";
import { showBrowserNotification } from "../utils/browserNotification";
import { subscribeToPush } from "../utils/pushSubscription";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function requestGPS(onSuccess, onError) {
  if (!navigator.geolocation) {
    onError("Geolocation not supported");
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => onSuccess({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
    (err) => onError(err),
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

export function useDashboardData() {
  const [alerts, setAlerts] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [proximityRadius, setProximityRadius] = useState({ security: 10, fire: 10, medical: 10, accident: 10 });

  const syncLocationToServer = useCallback((loc) => {
    axios.post(`${API}/users/location`, loc, { withCredentials: true }).catch((err) => {
      logging.warn("Failed to sync location", err);
    });
  }, []);

  // Auto GPS on mount
  useEffect(() => {
    requestGPS(
      (loc) => { setUserLocation(loc); syncLocationToServer(loc); },
      (err) => logging.info("GPS unavailable, will try saved location", err)
    );
  }, [syncLocationToServer]);

  // Load user's proximity + saved location fallback
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data.proximity_radius != null) {
          setProximityRadius(res.data.proximity_radius);
        }
        // Fallback: use saved location if GPS didn't provide one
        setUserLocation((prev) => {
          if (prev) return prev; // GPS already set, keep it
          const lat = res.data.latitude;
          const lng = res.data.longitude;
          if (typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng)) {
            return { latitude: lat, longitude: lng };
          }
          return prev;
        });
      } catch { /* /auth/me 401 when not signed in — dashboard shows the login gate anyway */ }
    })();
  }, []);

  // Manual location (from map click)
  const setManualLocation = useCallback((loc) => {
    setUserLocation(loc);
    syncLocationToServer(loc);
  }, [syncLocationToServer]);

  // Retry GPS button
  const retryGPS = useCallback(() => {
    requestGPS(
      (loc) => { setUserLocation(loc); syncLocationToServer(loc); },
      (err) => logging.info("GPS retry failed", err)
    );
  }, [syncLocationToServer]);

  const updateProximity = useCallback(async (radiusMap) => {
    setProximityRadius(radiusMap);
    try {
      await axios.patch(`${API}/users/proximity`, radiusMap, { withCredentials: true });
    } catch (err) {
      logging.error("Failed to update proximity", err);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/alerts/active-map`, { withCredentials: true });
      setAlerts(res.data);
    } catch (err) {
      logging.error("Failed to fetch alerts", err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      logging.error("Failed to fetch stats", err);
    }
  }, []);

  const prevUnreadCount = useRef(-1);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      const newNotifs = res.data;
      const newUnread = newNotifs.filter((n) => !n.read).length;

      // Play alert sound when new unread notifications arrive (skip first load)
      if (prevUnreadCount.current >= 0 && newUnread > prevUnreadCount.current) {
        playAlertSound();
      }
      prevUnreadCount.current = newUnread;

      setNotifications(newNotifs);
    } catch (err) {
      logging.error("Failed to fetch notifications", err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchStats();
    fetchNotifications();
    // NOTE: We deliberately DO NOT call requestNotificationPermission()
    // on mount any more. Modern browsers — Android Chrome in particular
    // — silently *reject* `Notification.requestPermission()` when it is
    // not tied to a *transient user activation* (a real click/tap).
    // Permission is instead requested via the explicit "Enable ring
    // notifications" button on <DoorbellCard/>.
    //
    // BUT: if the resident has ALREADY granted permission (either on a
    // previous visit or via that button), we DO want to refresh their
    // push subscription on every dashboard mount. This handles three
    // real-world drift cases:
    //   1. The browser rotated their `PushSubscription.endpoint`
    //      (Firefox/Chrome do this occasionally for privacy).
    //   2. The backend garbage-collected their old row when a previous
    //      push returned 410 Gone (dead subscription).
    //   3. They installed the PWA fresh on a new device but the tab is
    //      "logged in" from an imported profile.
    // `subscribeToPush()` is idempotent — it re-uses an existing
    // PushManager subscription when the endpoint matches, and only mints
    // a new one when needed. Crucially it does NOT prompt for permission
    // if permission is already `granted` — so this is safe to run in an
    // effect without any user gesture.
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "granted" &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    ) {
      // Give the SW a moment to be ready before subscribing — fire-and-
      // forget, failure logs internally.
      const t = setTimeout(() => { subscribeToPush(); }, 500);
      // Return combined cleanup below via the shared cleanup path.
      // Track via closure:
      const interval = setInterval(() => {
        fetchAlerts();
        fetchNotifications();
      }, 30000);
      return () => { clearInterval(interval); clearTimeout(t); };
    }
    const interval = setInterval(() => {
      fetchAlerts();
      fetchNotifications();
    }, 30000);
    return () => { clearInterval(interval); };
  }, [fetchAlerts, fetchStats, fetchNotifications]);

  // WebSocket: real-time alert updates
  const handleWsMessage = useCallback((data) => {
    if (data.type === "new_alert") {
      // Add new alert to the list instantly
      setAlerts((prev) => [data.alert, ...prev]);
      fetchStats();
      fetchNotifications();
      // Compute distance from user's current position so the TTS voice
      // can say "350 metres from you" in the user's chosen language.
      const alert = data.alert;
      const here = userLocation;
      const dM = here && alert
        ? distanceMeters(here, { latitude: alert.latitude, longitude: alert.longitude })
        : undefined;
      playSoundForAlert(alert?.alert_type, alert?.description, Number.isFinite(dM) ? dM : undefined);
      showBrowserNotification(alert);
    } else if (data.type === "alert_resolved") {
      // Remove resolved alert from active list
      setAlerts((prev) => prev.filter((a) => a.alert_id !== data.alert_id));
      fetchStats();
    } else if (data.type === "alert_deleted") {
      setAlerts((prev) => prev.filter((a) => a.alert_id !== data.alert_id));
      fetchStats();
    } else if (data.type === "alerts_cleared") {
      setAlerts([]);
      fetchStats();
    } else if (data.type === "alert_escalated") {
      // Update escalation level in-place
      setAlerts((prev) => prev.map((a) =>
        a.alert_id === data.alert_id ? { ...a, escalation_level: data.escalation_level } : a
      ));
      fetchNotifications();
      playAlertSound();
      // When an alert escalates *to* CRITICAL (level >= 2), surface an
      // OS notification — these are the ones that admins absolutely
      // cannot afford to miss while on another tab. We fetch the alert
      // from current state to enrich the payload (type + description).
      if ((data.escalation_level || 0) >= 2) {
        setAlerts((prev) => {
          const a = prev.find((x) => x.alert_id === data.alert_id);
          if (a) showBrowserNotification({ ...a, escalation_level: data.escalation_level });
          return prev;
        });
      }
    } else if (data.type === "alert_assigned") {
      setAlerts((prev) => prev.map((a) =>
        a.alert_id === data.alert_id ? { ...a, assigned_to: data.assigned_to, assigned_name: data.assigned_name } : a
      ));
    } else if (data.type === "alert_updated") {
      // Mostly for reverse-geocoded address slotting in after creation.
      // Generic enough that we patch any field present in the payload.
      const { type, alert_id, ...patch } = data;
      setAlerts((prev) => prev.map((a) =>
        a.alert_id === alert_id ? { ...a, ...patch } : a
      ));
    } else if (data.type === "live_room_started") {
      setAlerts((prev) => prev.map((a) =>
        a.alert_id === data.alert_id ? { ...a, live_room_active: true } : a
      ));
    } else if (data.type === "live_room_ended") {
      setAlerts((prev) => prev.map((a) =>
        a.alert_id === data.alert_id ? { ...a, live_room_active: false } : a
      ));
    }
    // `userLocation` is intentionally not a dep here — the WS callback
    // reads it from the closure only for a one-shot distance calc; if
    // we added it, `handleWsMessage` would re-create on every GPS tick
    // (every ~1 s in Driving Mode) and re-subscribe the WebSocket,
    // dropping every real-time update in flight. Reading a stale value
    // is safe: distance is a nice-to-have for the spoken TTS, not a
    // correctness requirement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats, fetchNotifications]);

  const { connected: wsConnected } = useWebSocket(handleWsMessage);

  return {
    alerts,
    userLocation,
    stats,
    notifications,
    proximityRadius,
    updateProximity,
    setManualLocation,
    retryGPS,
    wsConnected,
    fetchAlerts,
    fetchStats,
    fetchNotifications,
  };
}
